import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { qk } from '@/api/queries';
import type { DomainValues, SecretState, Section } from '@/api/types';
import { Button, Drawer, FieldHint, IconButton, Input, Segmented, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { patchSection } from './api';
import { SecretField } from './SecretField';
import s from './settings.module.css';

const REGISTRARS = ['none', 'ahost', 'suvan', 'openprovider', 'fake'] as const;
type Registrar = (typeof REGISTRARS)[number];

/** One row of the registrar price table (P7-A01, S4 §4: no registrar has a quote API), UZS a year. */
export type DomainPrice = NonNullable<DomainValues['prices']>[number];
type PriceDraft = { registrar: DomainPrice['registrar']; zone: string; register: string; renew: string; redemption: string; as_of: string };

const draftOf = (p: DomainPrice): PriceDraft => ({ registrar: p.registrar, zone: p.zone, register: String(p.register_uzs), renew: String(p.renew_uzs), redemption: p.redemption_uzs === null ? '' : String(p.redemption_uzs), as_of: p.as_of });

/**
 * «Доменные провайдеры» → «Изменить» (P7-B01, additive; the AI drawer's pattern): the registrar, its API user and
 * key (write-only: {set, masked} from the server, a new value only when typed), the PM's monthly domain budget,
 * the wildcard TLS date and — once the API carries it (P7-A01) — the price table the quotes come from.
 */
export function DomainsEditDrawer({ data, onClose }: { data: Section<DomainValues>; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const tv = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const toast = useToast();
  const qc = useQueryClient();
  const v = data.values;
  const [registrar, setRegistrar] = useState<Registrar>(v.registrar);
  const [apiUser, setApiUser] = useState(v.registrar_api_user ?? '');
  const [budget, setBudget] = useState(String(v.pm_monthly_budget_uzs));
  const [until, setUntil] = useState(v.wildcard_tls_until ?? '');
  const [prices, setPrices] = useState<PriceDraft[] | null>(v.prices ? v.prices.map(draftOf) : null);
  const [typed, setTyped] = useState('');
  const [clearing, setClearing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const secrets = data.secrets as Partial<Record<'registrar_api_key', SecretState>>;

  const pricesOut = (): DomainPrice[] | null =>
    prices?.map((p) => ({ registrar: p.registrar, zone: p.zone.trim().toLowerCase().replace(/^\./, ''), register_uzs: Number(p.register), renew_uzs: Number(p.renew), redemption_uzs: p.redemption.trim() ? Number(p.redemption) : null, as_of: p.as_of })) ?? null;

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (registrar !== v.registrar) body.registrar = registrar;
      if ((apiUser.trim() || null) !== (v.registrar_api_user ?? null)) body.registrar_api_user = apiUser.trim() || null;
      if (Number(budget) !== v.pm_monthly_budget_uzs) body.pm_monthly_budget_uzs = Number(budget);
      if ((until || null) !== (v.wildcard_tls_until ?? null)) body.wildcard_tls_until = until || null;
      const out = pricesOut();
      if (out && JSON.stringify(out) !== JSON.stringify(v.prices)) body.prices = out;
      if (typed.trim()) body.registrar_api_key = typed.trim();
      else if (clearing) body.registrar_api_key = null;
      return patchSection<DomainValues>('domains', body);
    },
    onSuccess: (next) => {
      setTyped('');
      qc.setQueryData(qk.settings('domains'), next);
      toast(t('domains_edit.saved'));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    if (!/^\d+$/.test(budget.trim())) return setProblem(t('domains_edit.budget_invalid'));
    const bad = prices?.find((p) => !/^[a-z0-9-]+(\.[a-z0-9-]+)*$/.test(p.zone.trim().toLowerCase().replace(/^\./, '')) || !/^\d+$/.test(p.register) || !/^\d+$/.test(p.renew) || (p.redemption.trim() && !/^\d+$/.test(p.redemption)) || !p.as_of);
    if (bad) return setProblem(t('domains_edit.price_invalid', { zone: bad.zone || '—' }));
    save.mutate();
  };
  const setPrice = (i: number, patch: Partial<PriceDraft>) => setPrices((xs) => (xs ? xs.map((x, j) => (j === i ? { ...x, ...patch } : x)) : xs));

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('domains_edit.title')}
      sub={t('domains_edit.sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="domain-settings" disabled={save.isPending}>
            {t('domains_edit.save')}
          </Button>
        </div>
      }
    >
      <form id="domain-settings" className={s.drawerForm} onSubmit={submit} noValidate>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('domains_edit.registrar')}</span>
          <Segmented
            variant="form"
            label={t('domains_edit.registrar')}
            value={registrar}
            onChange={setRegistrar}
            className={s.alignStart}
            options={REGISTRARS.map((k) => ({ value: k, label: tv(`domains.registrars.${k}`) }))}
          />
          <FieldHint>{tv(`domains_edit.registrar_hint.${registrar}`)}</FieldHint>
        </div>
        <Input label={t('domains_edit.api_user')} value={apiUser} onChange={(e) => setApiUser(e.target.value)} mono autoComplete="off" />
        <SecretField
          testId="registrar-key-state"
          labels={{
            label: t('domains_edit.api_key'),
            set: (masked) => t('ai_edit.key_set', { masked }),
            unset: t('ai_edit.key_unset'),
            replace: t('domains_edit.api_key_new'),
            clear: t('domains_edit.api_key_clear'),
            willClear: t('domains_edit.api_key_will_clear'),
          }}
          state={secrets.registrar_api_key}
          value={typed}
          clearing={clearing}
          onType={(value) => {
            setTyped(value);
            setClearing(false);
          }}
          onClear={() => setClearing((x) => !x)}
        />
        <div className={s.grid2}>
          <Input label={t('domains_edit.budget')} inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value)} />
          <Input label={t('domains_edit.tls_until')} type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        </div>
        <FieldHint>{t('domains_edit.budget_hint')}</FieldHint>
        {prices && (
          <div className={s.group}>
            <span className={s.groupLabel}>{t('domains_edit.prices')}</span>
            <FieldHint>{t('domains_edit.prices_hint')}</FieldHint>
            {prices.map((p, i) => (
              <div key={i} className={s.priceRow}>
                <select className={s.priceSelect} aria-label={t('domains_edit.price_registrar')} value={p.registrar} onChange={(e) => setPrice(i, { registrar: e.target.value as DomainPrice['registrar'] })}>
                  {REGISTRARS.filter((k) => k !== 'none').map((k) => (
                    <option key={k} value={k}>
                      {tv(`domains.registrars.${k}`)}
                    </option>
                  ))}
                </select>
                <Input aria-label={t('domains_edit.price_zone')} value={p.zone} onChange={(e) => setPrice(i, { zone: e.target.value })} mono height={44} />
                <Input aria-label={t('domains_edit.price_register')} inputMode="numeric" value={p.register} onChange={(e) => setPrice(i, { register: e.target.value })} height={44} />
                <Input aria-label={t('domains_edit.price_renew')} inputMode="numeric" value={p.renew} onChange={(e) => setPrice(i, { renew: e.target.value })} height={44} />
                <Input aria-label={t('domains_edit.price_as_of')} type="date" value={p.as_of} onChange={(e) => setPrice(i, { as_of: e.target.value })} height={44} />
                <IconButton icon="trash-2" label={t('domains_edit.price_remove', { zone: p.zone || '—' })} tone="outline" size={32} iconSize={14} onClick={() => setPrices((xs) => (xs ? xs.filter((_, j) => j !== i) : xs))} />
              </div>
            ))}
            <Button
              variant="secondary"
              size={34}
              icon="plus"
              iconSize={14}
              className={s.alignStart}
              onClick={() => setPrices((xs) => [...(xs ?? []), { registrar: registrar === 'none' ? 'ahost' : registrar, zone: 'uz', register: '', renew: '', redemption: '', as_of: new Date().toISOString().slice(0, 10) }])}
            >
              {t('domains_edit.price_add')}
            </Button>
          </div>
        )}
      </form>
    </Drawer>
  );
}
